// The Family Portal's invitations on the web (#770): the teacher's reading
// of each contact's access, and the four callables — enable or re-send,
// revoke, and the family's two on the join page.
//
// `portalAccess` is read with a plain listener rather than through the read
// store: it is written only by the server, so there is no queued write of
// this client's to overlay, and the read store's scope is the collections a
// teacher edits (src/data/readStore.js). Every write is a callable, online
// only: an invitation is a question with an answer (was it sent, was it
// refused, and why), and the answer is the server's.

import {
  collection, limit, onSnapshot, orderBy, query, where,
} from 'firebase/firestore';
import { db, functions } from '../lib/firebase';
import { familyPortalRefusal } from '../domain/familyPortal';

export const PORTAL_ACCESS = 'portalAccess';

// More contacts than a family has. A list read needs a limit to pass the
// rules (`withinListBound` in firestore.rules).
const ACCESS_LIMIT = 50;

/**
 * Calls `onRows([{ id, ...access }])` with the family's access records as they
 * change, and `onError(err)` when the read is refused or fails. Returns the
 * unsubscribe.
 */
export function watchPortalAccess(uid, familyId, onRows, onError = () => {}) {
  const q = query(
    collection(db, 'users', uid, PORTAL_ACCESS),
    where('familyId', '==', familyId),
    limit(ACCESS_LIMIT),
  );
  return onSnapshot(
    q,
    (snap) => onRows(snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }))),
    (err) => {
      console.warn('[portal] could not read the family’s portal access:', err);
      onError(err);
    },
  );
}

async function call(name, data) {
  if (!functions) throw new Error(familyPortalRefusal('failed'));
  const { httpsCallable } = await import('firebase/functions');
  try {
    const result = await httpsCallable(functions, name)(data);
    return result?.data ?? null;
  } catch (err) {
    // The server's sentence when it gave one (portal/invites.js), else the
    // core's catch-all; the refusal code rides along for a caller that
    // branches on it.
    const refusal = err?.details?.refusal || null;
    const said = refusal ? familyPortalRefusal(refusal) : '';
    const wrapped = new Error(said || err?.message || familyPortalRefusal('failed'));
    wrapped.refusal = refusal;
    wrapped.cause = err;
    throw wrapped;
  }
}

/** Enable a contact's access, or send the invitation again. */
export const inviteFamilyContact = ({ familyId, contactId }) => call('familyPortalInvite', { familyId, contactId });

/** Revoke a contact's access. */
export const revokeFamilyContact = ({ familyId, contactId }) => call('familyPortalRevoke', { familyId, contactId });

/** What a join link is: `{ studioName, email, hasLogin }`. */
export const describeJoinLink = (token) => call('familyPortalJoinInfo', { token });

/**
 * Redeem a join link: with a password while signed out (`{ customToken }`, or
 * `{ signIn: true }` when the address already has a login), or signed in as
 * the invited address (`{ bound: true }`).
 */
export const redeemJoinLink = ({ token, password = null }) => call('familyPortalRedeem', password == null ? { token } : { token, password });

/**
 * What a family sees of one studio's family on /portal (#772,
 * functions/portal/view.js): its students, their lessons on the family's
 * clock, the studio's contact lines and policy text. Refused, with the core's
 * sentence and `refusal` code, for a family this login may not see.
 */
export const loadFamilyPortalView = ({ studioUid, familyId }) => call('familyPortalView', { studioUid, familyId });

/**
 * Find & Book (#773, functions/portal/book.js): the open slots one studio's
 * family may book, on the family's clock, each of its students judged
 * against each slot, and each student's available make-up credits. With
 * `reschedule: { studentId, occurrenceId }` (#543), the times that student
 * may move that lesson to, each with the code the reschedule sends back.
 */
export const loadFamilyPortalSlots = ({ studioUid, familyId, reschedule = null }) => call(
  'familyPortalSlots',
  reschedule ? { studioUid, familyId, reschedule } : { studioUid, familyId },
);

/**
 * Book Now: one student into one occurrence. Resolves with what was booked
 * (`{ status: 'booked', slot, credit, priceMinor }`), or rejects with the
 * core's sentence and its `refusal` code — a slot just taken, too soon, not
 * enough credit.
 */
export const bookFamilyPortalSlot = ({ studioUid, familyId, studentId, occurrenceId }) => (
  call('familyPortalBook', { studioUid, familyId, studentId, occurrenceId })
);

/**
 * Cancel Booking (#775, functions/portal/cancel.js): one student off one
 * lesson under the studio's policy. `expected` is the code of what the
 * dialog said this cancel would do (the view's `lesson.cancel[studentId].code`);
 * the server refuses with `cancel_changed` when that is no longer what it
 * does. Resolves with `{ status: 'cancelled', side, attendance, text }`, or
 * rejects with the core's sentence and its `refusal` code.
 */
export const cancelFamilyPortalLesson = ({
  studioUid, familyId, studentId, occurrenceId, expected, note = null,
}) => call('familyPortalCancel', {
  studioUid, familyId, studentId, occurrenceId, expected, note,
});

/**
 * Reschedule (#543, functions/portal/reschedule.js): one student's lesson
 * `occurrenceId` moved to `toOccurrenceId`, both or neither. `expected` is
 * the code Find & Book's reschedule answer gave that time; the server
 * refuses with `reschedule_changed` when that is no longer what it does.
 * Resolves with `{ status: 'rescheduled', from, slot, credit }`, or rejects
 * with the core's sentence and its `refusal` code.
 */
export const rescheduleFamilyPortalLesson = ({
  studioUid, familyId, studentId, occurrenceId, toOccurrenceId, expected, note = null,
}) => call('familyPortalReschedule', {
  studioUid, familyId, studentId, occurrenceId, toOccurrenceId, expected, note,
});

// ── The teacher's Family Portal activity (#773, #775, #543) ────────────────
//
// `portalRequests` is written only by the server — the booking, cancel and
// reschedule callables and the shortfall settlement (functions/portal/) — so,
// like `portalAccess` above, a plain listener: there is no queued write of this
// client's to overlay. Newest first, bounded as every studio list is.

export const PORTAL_REQUESTS = 'portalRequests';
const REQUESTS_LIMIT = 100;

/**
 * Calls `onRows([{ id, ...request }])` with the studio's most recent Family
 * Portal requests as they change, and `onError(err)` when the read fails.
 * Returns the unsubscribe.
 */
export function watchPortalRequests(uid, onRows, onError = () => {}) {
  const q = query(
    collection(db, 'users', uid, PORTAL_REQUESTS),
    orderBy('createdAt', 'desc'),
    limit(REQUESTS_LIMIT),
  );
  return onSnapshot(
    q,
    (snap) => onRows(snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }))),
    (err) => {
      console.warn('[portal] could not read Family Portal activity:', err);
      onError(err);
    },
  );
}
